import { useWindowSize } from "hooks/useWindowSize";
import classNames from "classnames";
import { ConditionalLink } from "components/conditionalLink";
import { AmountSuperScript } from "components/design/AmountSuperScript";
import ConditionalToolTip from "components/design/conditionalToolTip";
import ToolTip from "components/design/toolTip";
import { TableUI } from "components/design/TableUI";
import Table from "components/DesignSystem/Table/V2/Table";
import { Avatar } from "components/DesignSystem/AvatarGroup/Avatar";
import { Button } from "components/DesignSystem/Button/Button";
import { Checkbox } from "components/DesignSystem/Checkbox/Checkbox";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import { HeadCellContent } from "components/DesignSystem/HeadCellContent/HeadCellContent";
import { Tag } from "components/DesignSystem/Tag/Tag";
import { Cross } from "components/icons/Cross";
import { DividerLine } from "components/icons/DividerLine";
import { FilterAndSort } from "components/icons/FilterSort";
import { Contacts as ContactsIcon } from "components/icons/LeftNav/Books/Contacts";
import { PdfFile } from "components/icons/PdfFile";
import { Preview } from "components/PreviewModal";
import { SelectedFloatingControl } from "components/SelectedFloatingControl/SelectedFloatingControl";
import { WithholdingStatusTag } from "components/VendorBills/WithholdingStatusTag";
import { USD } from "constants/currency";
import { DISPLAY_DATE } from "constants/date";
import { colors } from "constants/colors";
import {
  BILL_SOURCES_TYPE,
  BILL_STATUS,
  BILL_STATUS_LABEL,
  BILL_STATUS_GROUP_TYPES,
  BILLS_SOURCES,
  DATE_HEADER,
  FINANCIAL_RECORD_TYPE,
  STATUS_HEADER,
  TAG_TYPE,
} from "constants/vendorBills";
import dayjs from "dayjs";
import { useConstructInternalLink } from "hooks/useConstructInternalLink";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useModal } from "hooks/useModal";
import { useQuery } from "hooks/useQuery";
import { useMemo, useState } from "react";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  RowSelectionState,
  SortingState,
  useReactTable,
} from "react-table-8.10.7";
import { VendorBill } from "types/Models/vendorBills";
import { BillActionsDropdown } from "./BillActionsDropdown";
import { BillAiSuggestionIndicator } from "./BillAiSuggestionIndicator";
import { BillSlider } from "./BillSlider";
import TableSelectionControls from "./TableSelectionControls";
import { Info } from "components/icons/Info";
import { UpdateFilterFunction } from "hooks/useFilter";
import { VendorBillsFilterType } from "./VendorBillsFilter";
import { useGetAllBillsQuery } from "store/apis/vendorBills";
import { pluralize } from "utils/pluralize";
import { Mail } from "components/icons/Mail";
import { AnimatedNumbers } from "components/DesignSystem/AnimatedNumbers/AnimatedNumbers";
import { getCommonPinningStyles } from "utils/getCommonPinningStyles";

type BillListProps = {
  bills: VendorBill[];
  rowSelection?: RowSelectionState;
  setRowSelection?: (
    updaterOrValue:
      | RowSelectionState
      | ((prev: RowSelectionState) => RowSelectionState)
  ) => void;
  sorting: SortingState;
  setSorting: (
    updaterOrValue: SortingState | ((prev: SortingState) => SortingState)
  ) => void;
  updateFilter: UpdateFilterFunction<VendorBillsFilterType>;
  showWithholdingColumn?: boolean;
};

const columnHelper = createColumnHelper<VendorBill>();

const StatusHeader = () => {
  const query = useQuery();
  const billType = query.get("BILL_TYPE") || FINANCIAL_RECORD_TYPE.BILL;
  return STATUS_HEADER[billType as keyof typeof FINANCIAL_RECORD_TYPE];
};

const DateHeader = () => {
  const query = useQuery();
  const billType = query.get("BILL_TYPE") || FINANCIAL_RECORD_TYPE.BILL;

  return DATE_HEADER[billType as keyof typeof DATE_HEADER];
};

const InvoiceNumber = ({
  bill,
  openInNewTab,
}: {
  bill: VendorBill;
  openInNewTab?: boolean;
}) => {
  const { link } = useConstructInternalLink();
  const previewModal = useModal();

  const isBill = bill.bill_document_type === FINANCIAL_RECORD_TYPE.BILL;
  const isRequest = bill.bill_document_type === FINANCIAL_RECORD_TYPE.REQUEST;

  const billLink = isBill
    ? link(`/books/bills-and-vendors/bill/${bill.uuid}`, {
        moreQuery: { BILL_TYPE: FINANCIAL_RECORD_TYPE.BILL },
      })
    : isRequest
    ? link(`/books/bills-and-vendors/request/${bill.uuid}`, {
        moreQuery: { BILL_TYPE: FINANCIAL_RECORD_TYPE.REQUEST },
      })
    : "";

  return (
    <>
      <div className="t-flex t-min-w-0 t-max-w-60 t-items-center t-gap-1">
        {bill.bill_number ? (
          <ConditionalLink
            to={billLink}
            target={openInNewTab ? "_blank" : undefined}
            className="t-min-w-0"
          >
            <ToolTip text={bill.bill_number}>
              <Button
                customType="link"
                className="!t-shrink t-min-w-0 t-max-w-full [&_div]:t-min-w-0"
              >
                <span className="t-truncate">{bill.bill_number}</span>
              </Button>
            </ToolTip>
          </ConditionalLink>
        ) : (
          "-"
        )}
        {bill.bill_document?.uuid && (
          <Button
            size="small"
            customType="ghost_icon"
            onClick={(e) => {
              e.stopPropagation();
              previewModal.open();
            }}
          >
            <span className="t-text-text-30">
              <PdfFile />
            </span>
          </Button>
        )}
      </div>

      <Preview
        closeModal={previewModal.close}
        showModal={previewModal.isOpen}
        previewId={bill.bill_document?.uuid}
      />
    </>
  );
};

export const billColumns = {
  bill_select: (size = 1) =>
    columnHelper.accessor("bill_date", {
      header: ({ table }) => (
        <Checkbox
          checked={table.getIsAllRowsSelected()}
          indeterminate={table.getIsSomeRowsSelected()}
          onChange={table.getToggleAllRowsSelectedHandler()}
          name="select-all-bill-rows"
          disabled={table
            .getRowModel()
            .rows.every((row) => !row.getCanSelect())}
        />
      ),
      id: "selection",
      size,
      enableSorting: false,
      cell: ({ row }) => {
        return (
          <span>
            <Checkbox
              checked={row.getIsSelected()}
              indeterminate={row.getIsSomeSelected()}
              onChange={row.getToggleSelectedHandler()}
              disabled={!row.getCanSelect()}
              name={row.original.uuid}
              onClick={(e) => e.stopPropagation()}
            />
          </span>
        );
      },
    }),
  bill_date: (size = 15) =>
    columnHelper.accessor("bill_date", {
      id: "INVOICE_DATE",
      header: (header) => {
        const handler = header.column.getToggleSortingHandler() || (() => {});

        return (
          <div className="t-group">
            <HeadCellContent
              title="Bill Date"
              sort={{
                nextSortOrder: header.column.getNextSortingOrder(),
                isSorted: header.column.getIsSorted(),
                sortingHandler: handler,
              }}
            />
          </div>
        );
      },
      size,
      enableSorting: true,
      cell: ({ getValue, row }) => {
        const date = getValue();
        return (
          <div className="t-text-body-sm t-font-medium t-text-text-60">
            {date ? `${dayjs(getValue()).format(DISPLAY_DATE)}` : "-"}
          </div>
        );
      },
    }),

  bill_number: (size = 10) =>
    columnHelper.accessor("bill_number", {
      header: "BILL NUMBER",
      size,
      cell: ({ row, table }) => {
        const { openInNewTab } = (table.options.meta || {}) as {
          openInNewTab?: boolean;
        };
        return (
          <InvoiceNumber bill={row.original} openInNewTab={openInNewTab} />
        );
      },
    }),

  entity_merchant_data: (size = 15) =>
    columnHelper.accessor("entity_merchant_data", {
      id: "SENT_TO",
      header: (header) => {
        const handler = header.column.getToggleSortingHandler() || (() => {});

        return (
          <div className="t-group">
            <HeadCellContent
              title="Vendor"
              sort={{
                nextSortOrder: header.column.getNextSortingOrder(),
                isSorted: header.column.getIsSorted(),
                sortingHandler: handler,
              }}
            />
          </div>
        );
      },
      size,
      cell: ({ getValue }) => {
        const vendor = getValue();
        return (
          <>
            <div className="t-text-body-sm t-font-medium t-text-text-60">
              {vendor?.name || "-"}
            </div>
            <div className="t-text-text-30 t-text-body-sm">{vendor?.email}</div>
          </>
        );
      },
    }),

  due_balance: (size = 15, customHeader = "Amount") =>
    columnHelper.accessor("due_balance", {
      header: ({ table: { options } }) => {
        const meta = options.meta as {
          displayCurrency: string;
          setDisplayCurrency: (v: string) => void;
        };

        return (
          <p className="t-text-right t-m-0 t-w-full t-flex t-gap-1.5 t-items-center">
            <span className="t-ml-auto">{customHeader}</span>
            <Dropdown.Root>
              <Dropdown.Trigger asChild>
                <Button customType="ghost_icon" size="xsmall">
                  <FilterAndSort />
                </Button>
              </Dropdown.Trigger>
              <Dropdown.Portal>
                <Dropdown.Content
                  sideOffset={8}
                  side="bottom"
                  className="t-min-w-48"
                  align="end"
                >
                  <Dropdown.RadioGroup
                    value={meta.displayCurrency}
                    onValueChange={meta.setDisplayCurrency}
                  >
                    <Dropdown.RadioItem value="BILL_CURRENCY">
                      In bill's currency
                    </Dropdown.RadioItem>
                    <Dropdown.RadioItem value={USD}>In USD</Dropdown.RadioItem>
                  </Dropdown.RadioGroup>
                </Dropdown.Content>
              </Dropdown.Portal>
            </Dropdown.Root>
          </p>
        );
      },
      size,
      cell: ({ getValue, row, table }) => {
        const meta = table.options.meta as {
          displayCurrency: string;
          setDisplayCurrency: (v: string) => void;
        };

        const usdValue =
          row.original.payment_record_amount?.amount_in_base_currency;
        const due = getValue();
        const status =
          row.original.bill_document_type === FINANCIAL_RECORD_TYPE.BILL
            ? row.original.bill_payment_status
            : row.original.bill_request_status;
        const isPartiallyPaid = status === BILL_STATUS.PARTIALLY_PAID;
        const tooltipContent = isPartiallyPaid && (
          <div className="t-flex t-gap-9">
            <span>Amount pending:</span>
            <span>
              <AmountSuperScript
                amount={Number(row.original.pending_amount || 0)}
              />
            </span>
          </div>
        );

        return (
          <div className="t-flex t-items-center t-justify-end t-gap-2">
            <div className="t-text-body-sm t-font-medium t-text-right t-text-text-60">
              <AmountSuperScript
                amount={
                  meta.displayCurrency === USD
                    ? Number(usdValue || due || 0)
                    : Number(getValue() || 0)
                }
                currencyCode={
                  meta.displayCurrency === USD ? USD : row.original.currency
                }
              />
            </div>
            <ConditionalToolTip condition={tooltipContent}>
              <span>
                <Tag tagType={TAG_TYPE[status]} rounded icon={false}>
                  {BILL_STATUS_LABEL[status]}
                </Tag>
              </span>
            </ConditionalToolTip>
          </div>
        );
      },
    }),

  due_date: (size = 20, customHeader?: string) =>
    columnHelper.accessor("due_date", {
      id: "DUE_DATE",
      header: (header) => {
        const handler = header.column.getToggleSortingHandler() || (() => {});
        const title = customHeader || DateHeader().toString();
        return (
          <div className="t-group">
            <HeadCellContent
              title={title}
              sort={{
                nextSortOrder: header.column.getNextSortingOrder(),
                isSorted: header.column.getIsSorted(),
                sortingHandler: handler,
              }}
            />
          </div>
        );
      },
      size,
      enableSorting: true,
      cell: ({ row }) => {
        const date =
          row.original.bill_document_type === FINANCIAL_RECORD_TYPE.BILL
            ? row.original.due_date
            : row.original.bill_request_date;

        return (
          <div className="t-text-text-60 t-text-body-sm">
            {date ? `${dayjs(date).format(DISPLAY_DATE)}` : "-"}
          </div>
        );
      },
    }),

  bill_request_status: (size = 20) =>
    columnHelper.accessor("bill_request_status", {
      header: () => <StatusHeader />,
      size,
      cell: ({ row }) => {
        const status =
          row.original.bill_document_type === FINANCIAL_RECORD_TYPE.BILL
            ? row.original.bill_payment_status
            : row.original.bill_request_status;
        const isPartiallyPaid = status === BILL_STATUS.PARTIALLY_PAID;
        const tooltipContent = isPartiallyPaid && (
          <div className="t-flex t-gap-9">
            <span>Amount pending:</span>
            <span>
              <AmountSuperScript
                amount={Number(row.original.pending_amount || 0)}
              />
            </span>
          </div>
        );

        return (
          <ConditionalToolTip condition={tooltipContent}>
            <span>
              <Tag tagType={TAG_TYPE[status]} rounded icon={false}>
                {BILL_STATUS_LABEL[status]}
              </Tag>
            </span>
          </ConditionalToolTip>
        );
      },
    }),

  withholding: (size = 15) =>
    columnHelper.accessor("withholding_status", {
      header: () => (
        <p className="t-text-right t-m-0 t-w-full">Withheld Amount</p>
      ),
      size,
      cell: ({ row }) => {
        const { withholding_status, withheld_amount } = row.original;

        if (!withholding_status) {
          return (
            <div className="t-text-body-sm t-text-right t-text-text-30">-</div>
          );
        }

        return (
          <div className="t-flex t-items-center t-justify-end t-gap-2">
            <div className="t-text-body-sm t-font-medium t-text-right t-text-text-60">
              <AmountSuperScript amount={Number(withheld_amount || 0)} />
            </div>
            <WithholdingStatusTag status={withholding_status} />
          </div>
        );
      },
    }),

  source: (size = 20) =>
    columnHelper.accessor("source", {
      header: () => "SOURCE",
      size,
      cell: ({ row }) => {
        const sourceValue = row.original.source;
        const sourceConfig = BILLS_SOURCES[sourceValue as BILL_SOURCES_TYPE];
        const isManual = sourceValue === BILLS_SOURCES.MANUAL.value;

        return (
          <div className="t-flex t-justify-center">
            <ConditionalToolTip
              condition={sourceConfig !== undefined}
              text={sourceConfig?.label || ""}
            >
              <span>
                {sourceConfig?.icon === "ICON" ? (
                  <span
                    className="t-rounded-full t-bg-white t-overflow-hidden t-min-h-6 t-min-w-6 t-max-h-6 t-max-w-6 t-text-neutral-40 t-border-neutral-0 t-border t-border-solid t-flex t-items-center t-justify-center"
                    role="img"
                  >
                    {isManual ? <ContactsIcon /> : <Mail />}
                  </span>
                ) : (
                  <Avatar
                    size="small"
                    src={sourceConfig?.icon}
                    alt={sourceConfig?.label || ""}
                  />
                )}
              </span>
            </ConditionalToolTip>
          </div>
        );
      },
    }),

  title: (size = 5) =>
    columnHelper.accessor("title", {
      header: "Title",
      size,
      cell: (info) => {
        return (
          <div className="t-text-text-60 t-text-body-sm">{info.getValue()}</div>
        );
      },
    }),

  // Merged sticky column: AI suggestion indicator + the three-dot actions
  // menu share one pinned column (per the Figma "Frame with auto layout"
  // spec) instead of two separately pinned columns.
  aiAndActions: (size = 90) =>
    columnHelper.accessor("uuid", {
      id: "aiAndActions",
      header: "",
      size,
      enableSorting: false,
      cell: ({ row }) => (
        <div className="t-flex t-items-center t-gap-1 t-pl-2.5">
          <BillAiSuggestionIndicator bill={row.original} />
          <BillActionsDropdown bill={row.original} />
        </div>
      ),
    }),
};

const getColumns = (showWithholdingColumn?: boolean) => [
  billColumns.bill_select(48),
  billColumns.bill_number(140),
  billColumns.entity_merchant_data(200),
  billColumns.bill_date(120),
  billColumns.due_date(120),
  billColumns.due_balance(160, "Bill Amount"),
  ...(showWithholdingColumn ? [billColumns.withholding(180)] : []),
  billColumns.aiAndActions(90),
];

export const BillList = ({
  bills,
  rowSelection,
  setRowSelection,
  sorting,
  setSorting,
  updateFilter,
  showWithholdingColumn,
}: BillListProps) => {
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const slider = useModal();
  const { isMobile } = useWindowSize();
  const [billId, setBillId] = useState<string | undefined>();
  const [displayCurrency, setDisplayCurrency] = useState<
    "BILL_CURRENCY" | typeof USD
  >("BILL_CURRENCY");

  const columns = useMemo(
    () => getColumns(showWithholdingColumn),
    [showWithholdingColumn]
  );

  const { data: forwardedBillsData } = useGetAllBillsQuery(
    {
      bill_type: FINANCIAL_RECORD_TYPE.BILL,
      entityId,
      groupId: groupId!,
      page_num: 1,
      search_term: null,
      limit: 1,
    },
    {
      skip: !groupId || !entityId,
    }
  );

  const forwardedBillsCount =
    forwardedBillsData?.bills_forwarded_via_email_in_draft_state_count || 0;

  const table = useReactTable({
    data: bills,
    columns,
    defaultColumn: {
      minSize: 5,
    },
    getCoreRowModel: getCoreRowModel(),
    meta: {
      displayCurrency,
      setDisplayCurrency,
    },
    onRowSelectionChange: setRowSelection,
    getRowId: (row) =>
      JSON.stringify({
        uuid: row.uuid,
        due_balance: row.due_balance,
        amount_in_base_currency:
          row.payment_record_amount?.amount_in_base_currency ?? null,
      }),
    state: {
      rowSelection,
      sorting,
      columnPinning: isMobile ? {} : { right: ["aiAndActions"] },
    },
    manualSorting: true,
    enableMultiSort: false,
    enableSortingRemoval: false,
    onSortingChange: setSorting,
  });

  const onSelectionChange = (billIds?: string[]) => {
    if (setRowSelection) {
      setRowSelection((prev) => {
        const newSelection: RowSelectionState = {};
        billIds?.forEach((id) => {
          newSelection[id] = true;
        });
        return newSelection;
      });
    }
  };
  const allSelectedBills = useMemo(
    () =>
      Object.entries(rowSelection ?? {})
        .filter(([_, isSelected]) => isSelected)
        .map(
          ([key]) =>
            JSON.parse(key) as {
              uuid: string;
              due_balance: string | null;
              amount_in_base_currency: string | null;
            }
        ),
    [rowSelection]
  );

  const selectedBills = useMemo(
    () => allSelectedBills.map((row) => row.uuid),
    [allSelectedBills]
  );

  const uuidRowSelection = useMemo(
    () => Object.fromEntries(selectedBills.map((uuid) => [uuid, true])),
    [selectedBills]
  );

  const subtotal = useMemo(
    () =>
      allSelectedBills.reduce(
        (sum, row) =>
          sum + Number(row.amount_in_base_currency || row.due_balance || 0),
        0
      ),
    [allSelectedBills]
  );

  const openSlider = ({ billId }: { billId: string }) => {
    setBillId(billId);
    slider.open();
  };
  const closeSlider = () => {
    setBillId(undefined);
    slider.close();
  };

  const handleRowClick = (uuid: string) => {
    openSlider({
      billId: uuid,
    });
  };

  return (
    <div className="t-h-full t-flex t-flex-col t-min-h-0 t-overflow-hidden sm:t-h-auto sm:t-overflow-visible">
      {forwardedBillsCount > 0 && (
        <div className="t-rounded-lg t-bg-[#FAFAFA] t-border t-border-solid t-border-neutral-10 t-p-2.5 t-flex t-items-center t-gap-2 t-text-black-30 t-mb-4 t-w-full sm:t-flex-wrap">
          <Info color={colors.neutral[70]} />
          <div className="t-text-body-sm t-text-text-30 t-flex-1">
            {pluralize(forwardedBillsCount, "bill", "bills")} forwarded via
            email {pluralize(forwardedBillsCount, "is", "are", false)} in draft
            section. You can review and mark{" "}
            {pluralize(forwardedBillsCount, "it", "them", false)} as active.
          </div>
          <Button
            size="small"
            onClick={() => {
              updateFilter("SOURCE", [BILLS_SOURCES.FORWARDED_VIA_EMAIL.value]);
              updateFilter("STATUS_GROUP", BILL_STATUS_GROUP_TYPES.DRAFT);
            }}
          >
            Review
          </Button>
        </div>
      )}
      <TableUI
        table={table}
        sizeType="absolute"
        className="t-overflow-auto t-h-full t-pb-20 scrollbar-visible sm:t-h-auto"
        header={({ headerGroup }) => (
          <Table.HeadRow key={headerGroup.id}>
            {headerGroup.headers.map((header) => {
              const isPinnedColumn = Boolean(header.column.getIsPinned());

              return (
                <Table.HeadCell
                  key={header.id}
                  isUpperCase
                  className={classNames({
                    "!t-bg-surface-lighter-grey t-drop-shadow-table-right-pin t-shadow-table-right-pin":
                      isPinnedColumn,
                  })}
                  style={{
                    minWidth: header.getSize(),
                    ...getCommonPinningStyles(header.column),
                  }}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                </Table.HeadCell>
              );
            })}
          </Table.HeadRow>
        )}
        children={({ row }) => (
          <Table.Row
            key={row.id}
            onRowClick={() => handleRowClick(row.original.uuid)}
            isRowSelected={row.getIsSelected()}
            className="t-group"
          >
            {row.getVisibleCells().map((cell) => {
              const isPinnedColumn = Boolean(cell.column.getIsPinned());

              return (
                <Table.Cell
                  key={cell.id}
                  className={classNames({
                    "t-drop-shadow-table-right-pin t-shadow-table-right-pin":
                      isPinnedColumn,
                    "group-hover:!t-bg-surface-lighter-grey": isPinnedColumn,
                    "!t-bg-purple--1": row.getIsSelected() && isPinnedColumn,
                  })}
                  style={{
                    minWidth: cell.column.getSize(),
                    maxWidth: cell.column.getSize(),
                    ...getCommonPinningStyles(cell.column),
                  }}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </Table.Cell>
              );
            })}
          </Table.Row>
        )}
      />
      {billId && (
        <BillSlider
          close={closeSlider}
          isOpen={slider.isOpen}
          billId={billId}
        />
      )}

      <SelectedFloatingControl>
        {selectedBills.length > 0 && (
          <>
            <span className="t-whitespace-nowrap t-text-subtext t-text-text-60">
              {selectedBills.length} selected
            </span>
            <span className="t-text-neutral-10 t-h-full">
              <DividerLine />
            </span>
            {selectedBills.length > 0 && (
              <div className="t-flex t-items-center t-gap-1 t-text-text-60 t-text-subtext">
                <span>Subtotal:</span>

                <AnimatedNumbers amount={subtotal} />
              </div>
            )}
            <TableSelectionControls
              groupId={groupId!}
              entityId={entityId}
              selectedBills={selectedBills}
              rowSelection={uuidRowSelection}
              setRowSelection={setRowSelection}
              onSelectionChange={onSelectionChange}
              currentPage={1}
            />
            <Button
              customType="ghost_icon"
              size="xsmall"
              onClick={() => onSelectionChange([])}
            >
              <Cross />
            </Button>
          </>
        )}
      </SelectedFloatingControl>
    </div>
  );
};
